import { type ReactNode } from 'react';

interface Column<T> {
  key: string;
  label: string;
  hideOnMobile?: boolean;
  render: (item: T) => ReactNode;
}

interface ResponsiveTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyExtractor: (item: T) => string;
  mobileLabel?: (item: T) => string;
  renderMobileHeader?: (item: T) => ReactNode;
  onRowClick?: (item: T) => void;
  emptyMessage?: string;
}

/**
 * Responsive table that shows a data table on desktop and a card layout on mobile.
 */
export function ResponsiveTable<T>({
  columns,
  data,
  keyExtractor,
  mobileLabel,
  renderMobileHeader,
  onRowClick,
  emptyMessage,
}: ResponsiveTableProps<T>) {
  if (data.length === 0 && emptyMessage) {
    return <p className="py-6 text-center text-sm text-text-muted">{emptyMessage}</p>;
  }

  const rowClickable = !!onRowClick;

  return (
    <>
      {/* Desktop table */}
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-text-muted">
              {columns.map((col) => (
                <th key={col.key} className="px-3 py-3">
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((item) => (
              <tr
                key={keyExtractor(item)}
                className={`border-t border-border ${
                  rowClickable ? 'cursor-pointer hover:bg-surface-elevated' : ''
                }`}
                onClick={rowClickable ? () => onRowClick(item) : undefined}
              >
                {columns.map((col) => (
                  <td key={col.key} className="px-3 py-3">
                    {col.render(item)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="flex flex-col gap-3 md:hidden">
        {data.map((item) => (
          <div
            key={keyExtractor(item)}
            className={`rounded-xl border border-border bg-surface-elevated p-4 ${
              rowClickable ? 'cursor-pointer active:opacity-80' : ''
            }`}
            onClick={rowClickable ? () => onRowClick(item) : undefined}
          >
            {renderMobileHeader ? (
              <div className="mb-3">{renderMobileHeader(item)}</div>
            ) : mobileLabel ? (
              <p className="mb-2 text-sm font-semibold text-text">{mobileLabel(item)}</p>
            ) : null}
            <div className="flex flex-col gap-3">
              {columns
                .filter((col) => !col.hideOnMobile)
                .map((col) => (
                  <div
                    key={col.key}
                    className="flex items-center justify-between gap-3"
                  >
                    <span className="shrink-0 text-xs font-medium uppercase tracking-wide text-text-muted">
                      {col.label}
                    </span>
                    <div className="flex min-w-0 flex-1 justify-end text-sm text-text">
                      {col.render(item)}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
